/**
 * Unit systems for distances and speeds. The backend reports metres and mph;
 * everything here converts for display only.
 */

export const UNIT_SYSTEMS = Object.freeze({ METRIC: 'metric', IMPERIAL: 'imperial' })

const METRES_PER_MILE = 1609.344
const FEET_PER_METRE = 3.280839895
const KMH_PER_MPH = 1.609344

const DISTANCE_UNIT = { [UNIT_SYSTEMS.METRIC]: 'km', [UNIT_SYSTEMS.IMPERIAL]: 'mi' }

// IANA zones inside the United States. Only consulted when no browser
// language carries a region, e.g. a bare "en".
const US_TIME_ZONES = new Set([
  'America/Adak',
  'America/Anchorage',
  'America/Boise',
  'America/Chicago',
  'America/Denver',
  'America/Detroit',
  'America/Juneau',
  'America/Los_Angeles',
  'America/Menominee',
  'America/Metlakatla',
  'America/New_York',
  'America/Nome',
  'America/Phoenix',
  'America/Sitka',
  'America/Yakutat',
  'Pacific/Honolulu',
])
const US_TIME_ZONE_PREFIXES = ['America/Indiana/', 'America/Kentucky/', 'America/North_Dakota/']

const oneDecimal = (n) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const regionOf = (languageTag) => {
  try {
    return new Intl.Locale(languageTag).region ?? null
  } catch {
    return null
  }
}

/**
 * The unit system a new user should start on: imperial when their browser
 * says it is in the US, metric everywhere else.
 * @param {{ languages?: readonly string[], timeZone?: string }} [env] - Defaults to the browser's
 * @returns {'metric'|'imperial'}
 */
export function detectDefaultUnitSystem({
  languages = typeof navigator === 'undefined' ? [] : navigator.languages,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
} = {}) {
  const regions = (languages ?? []).map(regionOf).filter(Boolean)
  if (regions.length > 0) {
    return regions.includes('US') ? UNIT_SYSTEMS.IMPERIAL : UNIT_SYSTEMS.METRIC
  }
  const inUs =
    US_TIME_ZONES.has(timeZone) || US_TIME_ZONE_PREFIXES.some((p) => timeZone?.startsWith(p))
  return inUs ? UNIT_SYSTEMS.IMPERIAL : UNIT_SYSTEMS.METRIC
}

/** The unit a distance chart axis is drawn in: 'km' or 'mi'. */
export const distanceUnit = (unitSystem = UNIT_SYSTEMS.METRIC) =>
  DISTANCE_UNIT[unitSystem] ?? DISTANCE_UNIT[UNIT_SYSTEMS.METRIC]

/**
 * A distance as a plain number of km or mi, for plotting.
 * @param {number} metres
 * @param {'metric'|'imperial'} [unitSystem='metric']
 * @returns {number}
 */
export function distanceInUnits(metres, unitSystem = UNIT_SYSTEMS.METRIC) {
  const m = Number(metres) || 0
  return unitSystem === UNIT_SYSTEMS.IMPERIAL ? m / METRES_PER_MILE : m / 1000
}

/**
 * A distance as a display number and its unit, so callers can style them
 * separately. Metric: whole metres under 1 km, then km to one decimal.
 * Imperial: whole feet under a tenth of a mile, then miles to one decimal.
 * @param {number} metres
 * @param {'metric'|'imperial'} [unitSystem='metric']
 * @returns {{ value: string, unit: string }} e.g. { value: '66.5', unit: 'mi' }
 */
export function distanceParts(metres, unitSystem = UNIT_SYSTEMS.METRIC) {
  const m = Number(metres) || 0
  if (unitSystem === UNIT_SYSTEMS.IMPERIAL) {
    const miles = m / METRES_PER_MILE
    if (miles < 0.1) return { value: String(Math.round(m * FEET_PER_METRE)), unit: 'ft' }
    return { value: oneDecimal(miles), unit: 'mi' }
  }
  if (m < 1000) return { value: String(Math.round(m)), unit: 'm' }
  return { value: oneDecimal(m / 1000), unit: 'km' }
}

/**
 * Format a distance for display: "850 m", "1.2 km", "43 ft", "66.5 mi".
 * @param {number} metres
 * @param {'metric'|'imperial'} [unitSystem='metric']
 * @returns {string}
 */
export function formatDistance(metres, unitSystem = UNIT_SYSTEMS.METRIC) {
  const { value, unit } = distanceParts(metres, unitSystem)
  return `${value} ${unit}`
}

/**
 * Format a speed the backend reports in mph, to one decimal in the chosen system.
 * @param {number} mph
 * @param {'metric'|'imperial'} [unitSystem='metric']
 * @returns {string}
 */
export function formatSpeed(mph, unitSystem = UNIT_SYSTEMS.METRIC) {
  const v = Number(mph) || 0
  return unitSystem === UNIT_SYSTEMS.IMPERIAL
    ? `${oneDecimal(v)} mph`
    : `${oneDecimal(v * KMH_PER_MPH)} km/h`
}
